import React from 'react';

import SvgIcon from '../../SvgIcon/SvgIcon';

import { IconKey } from '../../../types/system/icons';

interface POSInputFieldProps {
  label?: string;

  name?: string;

  type?: 'text' | 'number' | 'email' | 'password' | 'date';

  value?: string | number;

  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;

  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;

  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

  error?: string;

  min?: number;

  max?: number;

  step?: string;

  placeholder?: string;

  icon?: IconKey;

  disabled?: boolean;

  required?: boolean;

  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];

  pattern?: string;

  rightContent?: React.ReactNode;

  className?: string;

  inputClassName?: string;
}

const POSInputField: React.FC<POSInputFieldProps> = ({
  label,

  name,

  type = 'text',

  value,

  onChange,

  onKeyDown,

  onFocus,

  onBlur,

  error,

  min,

  max,

  step,

  placeholder,

  icon,

  disabled = false,

  required = false,

  inputMode,

  pattern,

  rightContent,

  className = '',

  inputClassName = '',
}) => {
  return (
    <div
      className={`
      flex
      w-full
      flex-col
      gap-1

      ${className}
    `}
    >
      {/* ===================================================== */}
      {/* 🔹 Label */}
      {/* ===================================================== */}

      {label && (
        <label
          className="
          flex
          items-center
          gap-1

          text-xs
          font-medium

          text-body
        "
        >
          {label}

          {required && <span className="text-danger">*</span>}
        </label>
      )}

      {/* ===================================================== */}
      {/* 🔹 Field */}
      {/* ===================================================== */}

      <div
        className={`
        flex
        h-[44px]
        w-full
        items-center
        overflow-hidden

        rounded-xl

        border

        transition-all

        ${
          disabled
            ? `
              border-stroke
              bg-whiten
            `
            : `
              border-stroke
              bg-white

              focus-within:border-primary

              focus-within:ring-2
              focus-within:ring-primary/10
              hover:border-primary/30
            `
        }

        dark:border-strokedark
        dark:bg-boxdark
      `}
      >
        {/* ===================================================== */}
        {/* 🔹 Icon */}
        {/* ===================================================== */}

        {icon && (
          <div
            className="
            flex
            h-full
            w-[48px]
            shrink-0
            items-center
            justify-center

            border-r
            border-stroke

            text-body

            dark:border-strokedark
            dark:text-bodydark
          "
          >
            <SvgIcon name={icon} className="h-4 w-4" />
          </div>
        )}

        {/* ===================================================== */}
        {/* 🔹 Input */}
        {/* ===================================================== */}

        <input
          type={type}
          name={name}
          value={value ?? ''}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={disabled}
          inputMode={inputMode}
          pattern={pattern}
          className={`
          h-full
          flex-1

          bg-transparent

          px-4

          text-sm
          text-black

          outline-none

          placeholder:text-body

          disabled:cursor-not-allowed
          disabled:text-bodydark

          dark:text-white
          dark:placeholder:text-bodydark

          ${inputClassName}
        `}
        />

        {/* ===================================================== */}
        {/* 🔹 Right Content */}
        {/* ===================================================== */}

        {rightContent && (
          <div
            className="
            flex
            h-full
            items-center

            border-l
            border-stroke

            px-3

            dark:border-strokedark
          "
          >
            {rightContent}
          </div>
        )}
      </div>

      {/* ===================================================== */}
      {/* 🔹 Max Info */}
      {/* ===================================================== */}

      {max !== undefined && type === 'number' && (
        <p
          className="
          text-[11px]

          text-body
        "
        >
          Máximo permitido: {max}
        </p>
      )}

      {/* ===================================================== */}
      {/* 🔹 Error */}
      {/* ===================================================== */}

      {error && (
        <p
          className="
          text-[11px]
          font-medium

          text-danger
        "
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default POSInputField;
